import Component from '@glimmer/component'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import { getTabTypeGroupClasses } from 'teacher-ui-client/components/tab-group/tab-types'
import { hash } from '@ember/helper'
import { LinkToTabComponent } from 'teacher-ui-client/components/tab-group/link-to-tab'
import { TabComponent } from 'teacher-ui-client/components/tab-group/tab'

/**
 * // LinkTo tabs. active state is automatically applied thanks to LinkTo.
 * <TabGroup @theme="blue|green|etc.." @tabType="shadow|underline|border" as |tabGroup|>
 *   <tabGroup.linkToTab @route="abc" @models=(array "1")>
 *     First
 *   </tabGroup.linkToTab>
 *   <tabGroup.linkToTab @route="def">
 *     Second
 *   </tabGroup.linkToTab>
 * </TabGroup>
 *
 * // Standard tabs. You'll want to set the active state, and provide the click handler.
 * <TabGroup ... as |tabGroup|>
 *   <tabGroup.tab @isActive={{eq this.currentTab "tab-1"}} {{on "click" (fn this.changeTab "tab-1")}}>
 *     First
 *   </tabGroup.tab>
 * </TabGroup>
 *
 * // Alternatively, you can use the standard tabs with href, as they're just <a> anchor tags.
 * <TabGroup ... as |tabGroup|>
 *   <tabGroup.tab href="#section-1">
 *     Show me section 1
 *   </tabGroup.tab>
 * </TabGroup>
 *
 * Try to use a LinkTo tab when you can, as you get 'active' state management for free.
 *
 * // Themeing
 * You may theme either on the group or on a per-tab basis. per-tab will override. You may want to check the stylesheet
 * for what themes & tab types are available (and combinations of those)
 * <TabGroup @theme="blue" @tabType="shadow|underline|border" as |tabGroup|>
 *   <tabGroup.tab @theme="orange">
 *     First is orange
 *   </tabGroup.tab>
 *   <tabGroup.tab>
 *     Second is blue, due to the tab group theme
 *   </tabGroup.tab>
 * </TabGroup>
 */

interface Args {
  theme?: ThemeKeys
  tabType?: TAB_TYPE_KEYS
  isActive?: boolean
}

interface Signature {
  Args: Args
  Element: HTMLElement
  Blocks: {
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    default: [{ linkToTab: any; tab: any }]
  }
}

export class TabGroupComponent extends Component<Signature> {
  get tabTypeGroupClasses() {
    return this.args.tabType ? getTabTypeGroupClasses(this.args.tabType) : ''
  }

  <template>
    <nav data-test-tab-group-component={{@tabType}} class="{{this.tabTypeGroupClasses}}" role="tablist" ...attributes>
      {{yield
        (hash
          linkToTab=(component LinkToTabComponent theme=@theme tabType=@tabType)
          tab=(component TabComponent theme=@theme tabType=@tabType isActive=@isActive)
        )
      }}
    </nav>
  </template>
}

export default TabGroupComponent
